import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useSubjectVariablesEdit } from '@verantos-dev/app-state-cohort-builder';
import { ApiWorkflowDialog, RadioButton, Tooltip } from '@verantos-dev/shared-components';
import { FilterTypeLegacy, SubjectVariableTypeLegacy } from '@verantos-dev/types-cohorts';

import { useSubjectVariableEditCardActions } from './hooks/useSubjectVariableEditCardActions';
import { CohortBuilderEditModalActions } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalActions/CohortBuilderEditModalActions';
import { CohortBuilderEditModalFilter } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilter/CohortBuilderEditModalFilter';
import { CohortBuilderEditModalFilterSection } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilterSection/CohortBuilderEditModalFilterSection';
import { CohortBuilderEditModalSummary } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalSummary/CohortBuilderEditModalSummary';
import { CohortBuilderInputField } from '../../../../components/CohortBuilderInputField/CohortBuilderInputField';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import { getCohortEditModalTitle } from '../../../../utils/getCohortEditModalTitle';
import { getShouldShowFieldError } from '../../../../utils/getShouldShowFieldError';

export const SubjectVariableEditCard = () => {
  const theme = useTheme();
  const intl = useIntl();
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const updateFieldError = (id: string, error?: string) => {
    setFieldErrors((prev) => {
      const updatedErrors = { ...prev };
      if (error) {
        updatedErrors[id] = error;
      } else {
        delete updatedErrors[id];
      }
      return updatedErrors;
    });
  };

  // check if there are any errors in the simple event card
  const hasErrors = restrictSaveProcess
    ? Object.values(fieldErrors).some((error) => error !== undefined)
    : false;

  const {
    isSaveAllowed,
    onCancelSubjectVariable,
    onSaveSubjectVariable,
    onAddConceptFilterClick,
    onAddNumericFilterClick,
    name,
    type,
    modal,
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    onTypeChange,
    nameValidCharacters,

    enableAddFilterButtons,

    conceptFilters,
    numericFilters,
    countFilter,
    onFilterInputValueChange,
    onDeleteConceptFilterClick,
    onDeleteNumericFilterClick,

    inputFieldConfigMap,

    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,

    isInclusionCriteriaSelected,
    isExclusionCriteriaSelected,
    onSelectInclusionCriteria,
    onSelectExclusionCriteria,
    onSelectNotAppliedCriteria,

    isEditable,
    isProcessing,
  } = useSubjectVariablesEdit();
  const actions = useSubjectVariableEditCardActions({
    type,
    onAddConceptFilterClick,
    onAddNumericFilterClick,
  });

  const numberOfFilters = conceptFilters.length + numericFilters.length;
  const viewMode = !isEditable || isProcessing;

  /** Research Assistant Sync */
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);

  return (
    <ApiWorkflowDialog
      id="cohort-builder-subject-variable-edit-modal"
      title={getCohortEditModalTitle(intl, modal ?? null, !!isEditable)}
      size="large"
      isValidForSubmit={isSaveAllowed && !nameInError && !hasErrors}
      onlyShowCloseButton={viewMode}
      onClose={onCancelSubjectVariable}
      onSubmit={
        viewMode
          ? onCancelSubjectVariable
          : () => onSaveSubjectVariable(emitResearchAssistantUpdate)
      }
      dialogMessages={{
        entryCancelButtonText: viewMode
          ? intl.formatMessage({
              id: 'projects.cohortBuilderPage.subjectVariableEditCard.cancelButtonText',
            })
          : undefined,
      }}
      // onSubmit={onSubmit}
      // isSubmitting={isSaveStudyArmLoading}
      // submittingError={saveStudyArmError}
      dialogContentStyleProps={{ paddingBottom: 0 }}
      ref={dialogContentRef as any}
      NonScrollBottomComponent={
        <Box sx={{ px: theme.spacing(6) }}>
          {enableAddFilterButtons && !viewMode ? (
            <Box sx={(theme) => ({ pt: theme.spacing(4) })}>
              <CohortBuilderEditModalActions id="subject-variable" actions={actions} />
            </Box>
          ) : null}

          <Divider
            dir="horizontal"
            sx={{
              width: '100%',
              height: '1px',
              my: theme.spacing(4),
              color: theme.palette.grey[50],
            }}
          />

          <Box>
            <Typography variant="caption">
              {intl.formatMessage({
                id: 'projects.cohortBuilderPage.subjectVariableEditCard.ieCriteria.label',
              })}
            </Typography>
            <Box sx={{ flexDirection: 'row', display: 'flex', flexShrink: 1 }}>
              <RadioButton
                id="cohort-builder-criteria-inclusion"
                label={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.subjectVariableEditCard.ieCriteria.inclusion',
                })}
                width={'small'}
                checked={isInclusionCriteriaSelected}
                onChange={onSelectInclusionCriteria}
                disabled={!isEditable || isProcessing}
              />
              <RadioButton
                id="cohort-builder-criteria-exclusion"
                label={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.subjectVariableEditCard.ieCriteria.exclusion',
                })}
                width={'small'}
                checked={isExclusionCriteriaSelected}
                onChange={onSelectExclusionCriteria}
                disabled={!isEditable || isProcessing}
              />
              <RadioButton
                id="cohort-builder-criteria-disabled"
                label={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.subjectVariableEditCard.ieCriteria.notApplied',
                })}
                width={'auto'}
                checked={!isExclusionCriteriaSelected && !isInclusionCriteriaSelected}
                onChange={onSelectNotAppliedCriteria}
                disabled={!isEditable || isProcessing}
              />
            </Box>
          </Box>
        </Box>
      }
    >
      <Box data-testid={'header'} ref={headerRef}>
        <CohortBuilderEditModalSummary<SubjectVariableTypeLegacy>
          id="subject-variable"
          nameProps={{
            displayLabel: intl.formatMessage({
              id: 'projects.cohortBuilderPage.subjectVariableEditCard.nameLabel',
            }),
            placeholder: intl.formatMessage({
              id: 'projects.cohortBuilderPage.subjectVariableEditCard.namePlaceholder',
            }),
            value: name,
            error: nameInError,
            helperText: nameInError ? nameErrorMsg : undefined,
            onChange: onNameChange,
            onBlur: onNameBlur,
            validCharacters: nameValidCharacters,
            viewMode,
          }}
          typeProps={{
            id: 'simple-event-type',
            title: '',
            label: 'Type',
            placeholder: 'Select event type',
            value: type || '',
            onChange: (event) =>
              onTypeChange(event.target.name, event.target.value as SubjectVariableTypeLegacy),
            options: [SubjectVariableTypeLegacy.DEMOGRAPHICS, SubjectVariableTypeLegacy.COUNT],
            viewMode,
          }}
          viewMode={viewMode}
        />
      </Box>

      {enableAddFilterButtons || numberOfFilters > 0 ? (
        <Divider
          dir="horizontal"
          sx={{
            width: '100%',
            height: '1px',
            my: theme.spacing(4),
            color: theme.palette.grey[50],
          }}
        />
      ) : null}

      {/* render concept filters */}
      {conceptFilters.length > 0 && (
        <Box data-testid="conceptFilterSection" ref={conceptFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="concept-filters"
            key="concept-filters"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.subjectVariablesSection.conceptFilterSection.title',
            })}
            viewMode={viewMode}
          >
            {conceptFilters.map(({ id: conceptFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={conceptFilterId}
                  id={conceptFilterId}
                  onRemoveFilter={onDeleteConceptFilterClick}
                  viewMode={viewMode}
                >
                  {inputFields.map(({ id: inputFieldId, value, dirty }, filterIndex) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];
                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        conceptFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.CONCEPT
                      );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`subjectVariableEditCard-conceptFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                        multiple={filterIndex === 2}
                        showFieldError={dirty && getShouldShowFieldError(modal)}
                        updateFieldError={updateFieldError}
                        fieldErrorText={intl.formatMessage({
                          id: 'projects.cohortBuilderPage.inputFieldError.conceptGroupFieldError',
                        })}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
        </Box>
      )}

      {numberOfFilters > 0 && <Box style={{ height: '16px', width: '16px' }} />}

      {/* render numeric filters */}
      {numericFilters.length > 0 && (
        <Box data-testid="numericFilterSection" ref={numericFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="numeric-filters"
            key="numeric-filters"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.subjectVariablesSection.numericFilterSection.title',
            })}
            viewMode={viewMode}
          >
            {numericFilters.map(({ id: numericFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={numericFilterId}
                  id={numericFilterId}
                  onRemoveFilter={onDeleteNumericFilterClick}
                  viewMode={viewMode}
                >
                  {inputFields.map(({ id: inputFieldId, value }) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];
                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        numericFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.NUMERIC
                      );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`subjectVariableEditCard-numericFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
        </Box>
      )}

      {countFilter && (
        <CohortBuilderEditModalFilterSection
          id="count-filter"
          key="count-filter"
          title={intl.formatMessage({
            id: 'projects.cohortBuilderPage.subjectVariablesSection.countFilterSection.title',
          })}
          viewMode={viewMode}
        >
          <CohortBuilderEditModalFilter id={countFilter.id} viewMode={viewMode}>
            {countFilter.inputFields.map(({ id: inputFieldId, value }) => {
              if (!inputFieldConfigMap[inputFieldId]) {
                throw new Error(`Input field config not found for input field id: ${inputFieldId}`);
              }
              const { inputType, width, label, options, placeholder, helperText } =
                inputFieldConfigMap[inputFieldId];
              const onChange = (_value: string) => {
                onFilterInputValueChange(
                  countFilter.id,
                  inputFieldId,
                  _value,
                  FilterTypeLegacy.COUNT
                );
              };
              return (
                <CohortBuilderInputField
                  key={`subjectVariableEditCard-countFilter-${inputFieldId}`}
                  id={inputFieldId}
                  value={value}
                  inputType={inputType}
                  width={width}
                  label={label}
                  options={options}
                  placeholder={placeholder}
                  helperText={helperText}
                  onChange={onChange}
                  viewMode={viewMode}
                  readOnly={isProcessing}
                />
              );
            })}
          </CohortBuilderEditModalFilter>
        </CohortBuilderEditModalFilterSection>
      )}
    </ApiWorkflowDialog>
  );
};
